<template>
    <BaseDialog v-model:open="isOpen" :title="HEADER_TITLE" show-close float-close dialog-content-class="w-full md:w-140">
        <div
            v-if="showCelebration"
            class="min-h-84 max-h-150 flex flex-col justify-center items-center gap-3 px-6 py-10 text-center md:px-8"
            data-test-id="profile-link-celebration">
            <SuccessCelebration />
            <h4 class="nadia-subtitle text-valence-grey-850">Nice work!</h4>
            <p class="body-small max-w-sm leading-relaxed text-valence-grey-600">
                That's everyone for now — check back soon as Nadia surfaces more people to sharpen your insights.
            </p>
        </div>
        <template v-else>
            <div class="flex flex-col gap-1 px-6 pt-6 md:px-8 mb-8">
                <h3 class="flex items-center gap-2 display-serif text-valence-grey-850">
                    <i class="bi bi-stars text-lg text-valence-primary-500" aria-hidden="true"></i>
                    {{ HEADER_TITLE }}
                </h3>
                <p v-if="!showCelebration" class="body-small leading-relaxed text-valence-grey-600">{{ subtitle }}</p>
            </div>

            <!-- One shared grid across both sections so Link/Copy/Send button widths size off every row, not just their own section's -->
            <div class="grid max-h-150 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 px-6 pb-6 md:px-8 overflow-y-auto">
                <template v-for="section in sections" :key="section.key">
                    <ProfileLinkCreateProfilePrompt
                        v-if="section.key === 'share' && !hasCollaborationProfile"
                        :class="['col-span-2', { 'mt-5': linkSuggestions.length > 0 }]"
                        :locked-suggestion-count="shareSuggestions.length"
                        :profile-link-sharing-enabled="profileLinkSharingEnabled"
                        @shown="emit('create-profile-shown')"
                        @cta-click="emit('create-profile-click')"
                        @explainer-click="handleExplainerClick" />
                    <section v-else-if="section.items.length" class="contents">
                        <div :class="['col-span-2 flex min-h-9 items-center justify-between gap-3', { 'mt-5': section.key === 'share' && linkSuggestions.length > 0 }]">
                            <h4 class="title-body text-valence-grey-500">{{ section.title }}</h4>
                            <BaseButton
                                v-if="section.key === 'link' && canLinkAll"
                                theme="neutral"
                                class="relative flex items-center justify-center gap-2"
                                :disabled="isLinkingAll"
                                data-test-id="profile-link-all-btn"
                                @click="emit('link-all')">
                                <span :class="{ invisible: isLinkingAll }" class="inline-flex items-center gap-2">
                                    <i class="bi bi-stars text-valence-primary-500" aria-hidden="true"></i>
                                    Link all
                                </span>
                                <span v-if="isLinkingAll" class="absolute inset-0 flex items-center justify-center">
                                    <LoadingSpinner class="size-4" />
                                </span>
                            </BaseButton>
                        </div>
                        <HorizontalDivider class="col-span-2 mt-5 mb-1" />
                        <ProfileLinkSuggestionsRow
                            v-for="suggestion in section.items"
                            :key="suggestion.target_user_id"
                            :suggestion="suggestion"
                            :status="statusFor(suggestion)"
                            :own-profile-share="ownProfileShare"
                            :org-has-ms-teams="orgHasMsTeams"
                            :disabled="isLinkingAll"
                            @linked="(s) => emit('linked', s)"
                            @link-failed="(s) => emit('link-failed', s)"
                            @shared="(s, target) => emit('shared', s, target)"
                            @share-failed="(s, reason) => emit('share-failed', s, reason)"
                            @dismissed="(s) => emit('dismissed', s)"
                            @dismiss-failed="(s) => emit('dismiss-failed', s)" />
                    </section>
                </template>
            </div>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import SuccessCelebration from "~vue/components/SuccessCelebration.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, watch } from "vue";

import ProfileLinkCreateProfilePrompt from "./ProfileLinkCreateProfilePrompt.vue";
import ProfileLinkSuggestionsRow from "./ProfileLinkSuggestionsRow.vue";
import type { OwnProfileShare, ProfileLinkSuggestion, ShareFailureReason, ShareTarget, SuggestionStatus } from "./profileLinkTypes";

const props = defineProps<{
    suggestions: ProfileLinkSuggestion[];
    statuses: Record<string, SuggestionStatus>;
    showCelebration: boolean;
    ownProfileShare: OwnProfileShare | null;
    hasCollaborationProfile: boolean;
    orgHasMsTeams: boolean;
    profileLinkSharingEnabled: boolean;
    canLinkAll: boolean;
    isLinkingAll: boolean;
    surface: string;
}>();

const emit = defineEmits<{
    linked: [suggestion: ProfileLinkSuggestion];
    "link-failed": [suggestion: ProfileLinkSuggestion];
    shared: [suggestion: ProfileLinkSuggestion, target: ShareTarget];
    "share-failed": [suggestion: ProfileLinkSuggestion, reason: ShareFailureReason];
    dismissed: [suggestion: ProfileLinkSuggestion];
    "dismiss-failed": [suggestion: ProfileLinkSuggestion];
    "link-all": [];
    // Bubbled from ProfileLinkCreateProfilePrompt, past ProfileLinkSuggestionsCard, so the
    // mounting page can log its own contextual Amplitude event (the card is reused across surfaces).
    "create-profile-shown": [];
    "create-profile-click": [];
}>();

const isOpen = defineModel<boolean>("open", { default: false });

const HEADER_TITLE = "Sharpen your insights";

const statusFor = (suggestion: ProfileLinkSuggestion): SuggestionStatus => props.statuses[suggestion.target_user_id] ?? "idle";

const linkSuggestions = computed(() => props.suggestions.filter((s) => s.action === "link"));
const shareSuggestions = computed(() => props.suggestions.filter((s) => s.action === "share"));

const sections = computed(() => [
    { key: "link", title: "Link suggested profiles", items: linkSuggestions.value },
    { key: "share", title: "Share yours, request theirs", items: shareSuggestions.value },
]);

watch(isOpen, (open) => {
    if (!open) {
        return;
    }
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_MODAL_SHOWN, {
        link_count: linkSuggestions.value.length,
        share_count: shareSuggestions.value.length,
        collab_profile_cta_shown: !props.hasCollaborationProfile,
        surface: props.surface,
    });
});

function handleExplainerClick() {
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_EXPLAINER_CLICKED, { surface: props.surface });
}

const subtitle = computed(() => {
    // Sharing is unavailable org-wide: only ever talk about linking, never sharing your own profile.
    if (!props.profileLinkSharingEnabled) {
        return "Link colleagues who already have a Collaboration Profile to sharpen your insights.";
    }
    // Without a collaboration profile the user can't share, so steer them toward creating one.
    if (!props.hasCollaborationProfile) {
        return linkSuggestions.value.length === 0
            ? "Create your Collaboration Profile so Nadia can connect you with your team and sharpen your insights."
            : "Link colleagues who already have a profile, and create your own so you can share it back.";
    }
    return linkSuggestions.value.length === 0
        ? "These colleagues aren't on a shared profile yet — share yours so they can link it and send theirs back."
        : "Nadia's insights sharpen as you connect with your coworkers — link those with a profile, or share yours with those without.";
});
</script>
